import type {MarkersInfo} from '../stores/slices/markers'
import type {Comment, SuggestionDetails, Thread} from '@github-ui/diff-lines/conversation-types'
import type {BatchedSuggestion} from '../stores/slices/suggestion-batch'

type CommentWithThreadInfo = {
  comment: Comment
  path: string
  thread: Thread
}

/**
 * Builds a reverse index from comment database ID to comment, path, and thread.
 * This allows O(1) lookups instead of O(f*l*t*c) nested iterations.
 */
export function buildCommentIndex(markers: MarkersInfo | undefined): Map<number, CommentWithThreadInfo> {
  const index = new Map<number, CommentWithThreadInfo>()

  if (!markers) {
    return index
  }

  // Single pass through all markers to build the index
  for (const [filePath, lineMarkers] of Object.entries(markers)) {
    for (const lineMarker of Object.values(lineMarkers)) {
      for (const thread of lineMarker?.threads ?? []) {
        for (const comment of thread.commentsData?.comments ?? []) {
          if (comment.databaseId !== undefined && comment.databaseId !== null) {
            index.set(comment.databaseId, {comment, path: filePath, thread})
          }
        }
      }
    }
  }

  return index
}

/**
 * Gets detailed information about suggestions in the batch including their file paths
 */
export function getSuggestionDetails(
  markers: MarkersInfo | undefined,
  suggestionIds: BatchedSuggestion[],
): SuggestionDetails[] {
  if (!markers || suggestionIds.length === 0) {
    return []
  }

  const commentIndex = buildCommentIndex(markers)

  const details: SuggestionDetails[] = []

  for (const suggestionId of suggestionIds) {
    const found = commentIndex.get(suggestionId.commentDatabaseId)

    if (!found) {
      continue
    }

    details.push({
      commentDatabaseId: suggestionId.commentDatabaseId,
      suggestionIndex: suggestionId.suggestionIndex,
      path: found.path,
      bodyHTML: found.comment.bodyHTML,
    })
  }

  return details
}
